import type React from 'react'
import { useId, useState } from 'react'

export type InputTypeVariant = 'text' | 'phone' | 'address'

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  helperText?: string
  error?: string
  showCounter?: boolean
  maxLength?: number
  typeVariant?: InputTypeVariant
  countryCode?: string // for phone variant, e.g. "+44"
  onClear?: () => void
}

export const Input: React.FC<InputProps> = ({
  label = 'Label',
  helperText,
  error,
  showCounter = false,
  maxLength,
  typeVariant = 'text',
  countryCode = '+44',
  value = '',
  onChange,
  onClear,
  placeholder,
  className,
  ...rest
}) => {
  const id = useId()
  const [focused, setFocused] = useState(false)

  const hasValue = (value ?? '').toString().length > 0
  const showTopLabel = focused || hasValue

  const baseContainer = `relative rounded-[16px] px-4 pt-2.5 pb-3 bg-[rgba(24,24,27,0.02)]
    border border-transparent focus-within:border-[rgba(24,24,27,0.06)]`
  const errorContainer = error ? ' border-[#f30b26] focus-within:border-[#f30b26]' : ''

  return (
    <div className={className}>
      <div className={`${baseContainer}${errorContainer}`}>
        {label && (
          <label
            htmlFor={id}
            className={`block text-[13px] font-medium transition-all duration-150 ${showTopLabel ? 'text-zinc-400 mb-1 opacity-100 translate-y-0' : 'sr-only'}`}
          >
            {label}
          </label>
        )}

        <div className="flex items-center gap-2">
          {typeVariant === 'phone' && (
            <div className="flex items-center gap-2 pl-0 pr-2 text-[16px] font-semibold text-zinc-900">
              <span>{countryCode}</span>
              <span className="inline-block h-4 w-px bg-zinc-300" aria-hidden="true" />
            </div>
          )}

          <input
            id={id}
            value={value}
            onChange={onChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={showTopLabel ? undefined : (placeholder ?? label)}
            maxLength={maxLength}
            className={
              'flex-1 bg-transparent outline-none text-[16px] font-semibold text-zinc-900 placeholder:text-zinc-400'
            }
            {...rest}
          />

          {onClear && hasValue && (
            <button
              type="button"
              aria-label="入力をクリア"
              onClick={onClear}
              className="ml-1 inline-flex size-5 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-200/60"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="mt-1 flex items-start justify-between text-[13px]">
        <div className={error ? 'text-[#f30b26]' : 'text-zinc-500'}>{error ?? helperText}</div>
        {showCounter && typeof maxLength === 'number' && (
          <div className="text-zinc-400 tabular-nums">
            {String(value).length}/{maxLength}
          </div>
        )}
      </div>
    </div>
  )
}

export default Input
